import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiMail, FiLock, FiUser, FiAlertCircle, FiArrowRight, FiShield,
  FiCalendar, FiGlobe, FiCheckCircle, FiCheck, FiX, FiLoader, FiMoon, FiSun
} = FiIcons;

export default function Register() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '',
    nickname: '', firstName: '', lastName: '',
    dob: '', gender: 'Other', country: 'USA'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [nicknameStatus, setNicknameStatus] = useState('idle');
  const { register, globalOptions, checkNicknameAvailability, isDarkMode, toggleDarkMode } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (formData.nickname.length < 3) { setNicknameStatus('idle'); return; }
    const timer = setTimeout(async () => {
      setNicknameStatus('checking');
      const ok = await checkNicknameAvailability(formData.nickname);
      setNicknameStatus(ok ? 'available' : 'taken');
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.nickname]);

  const handleNext = () => {
    if (!formData.firstName || !formData.lastName || !formData.nickname || !formData.dob)
      return setError('Please fill all identity fields and date of birth');
    if (nicknameStatus === 'taken') return setError('Nickname is already taken');
    if (nicknameStatus === 'checking') return setError('Checking nickname availability...');
    setError('');
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) return setError('Passwords do not match');
    if (formData.password.length < 6) return setError('Password must be at least 6 characters');
    setLoading(true);
    const result = await register(formData);
    if (result.success) {
      result.confirmationRequired ? setVerificationSent(true) : navigate('/');
    } else {
      setError(result.message.includes('User already registered') ? 'This email address is already registered' : result.message);
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="min-h-screen bg-[#fcfaff] dark:bg-dark-bg flex items-center justify-center p-4 transition-colors duration-300">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white dark:bg-dark-surface rounded-[40px] p-10 shadow-2xl text-center border border-white dark:border-dark-border"
        >
          <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="text-4xl" />
          </div>
          <h2 className="text-2xl font-black text-brand-950 dark:text-white uppercase tracking-tight mb-4">Check your Email</h2>
          <p className="text-gray-500 dark:text-dark-muted font-medium mb-8">
            We've sent a verification link to <span className="font-bold text-brand-600 dark:text-brand-400">{formData.email}</span>.
          </p>
          <Link to="/login" className="inline-block w-full py-4 bg-brand-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-200 dark:shadow-brand-900/20">
            Back to Login
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fcfaff] dark:bg-dark-bg flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-300">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-200/30 dark:bg-brand-900/20 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand-100/30 dark:bg-brand-800/10 blur-[120px] rounded-full"></div>

      {/* Dark mode toggle */}
      <button
        onClick={toggleDarkMode}
        className="absolute top-5 right-5 p-2.5 rounded-xl bg-white dark:bg-dark-surface border border-gray-100 dark:border-dark-border text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-card transition-all shadow-sm z-10"
      >
        <SafeIcon icon={isDarkMode ? FiSun : FiMoon} className="w-5 h-5" />
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg relative z-10"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-brand-200 dark:shadow-brand-900/30 rotate-3">
            <span className="text-white font-black text-3xl leading-none tracking-tighter">N</span>
          </div>
          <h1 className="text-3xl font-black text-brand-950 dark:text-white tracking-tight mb-2 uppercase">Create Account</h1>
          <p className="text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest text-[9px]">Step {step} of 2 • Join the community</p>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-[40px] p-8 shadow-2xl shadow-brand-900/5 dark:shadow-black/20 border border-white dark:border-dark-border transition-colors">
          <form onSubmit={handleSubmit} className="space-y-5">
            <AnimatePresence mode="wait">
              {step === 1 ? (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <InputGroup label="First Name" icon={FiUser} value={formData.firstName} onChange={v => setFormData({ ...formData, firstName: v })} placeholder="John" />
                    <InputGroup label="Last Name" icon={FiUser} value={formData.lastName} onChange={v => setFormData({ ...formData, lastName: v })} placeholder="Doe" />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Platform Nickname</label>
                    <div className="relative group">
                      <SafeIcon icon={FiShield} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-600 transition-colors" />
                      <input
                        type="text" required value={formData.nickname}
                        onChange={e => setFormData({ ...formData, nickname: e.target.value })}
                        placeholder="ProGamer99"
                        className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 dark:bg-dark-card border rounded-2xl font-bold text-sm outline-none transition-all dark:text-white dark:placeholder-gray-600 ${
                          nicknameStatus === 'taken' ? 'border-red-300 dark:border-red-700'
                          : nicknameStatus === 'available' ? 'border-emerald-300 dark:border-emerald-700'
                          : 'border-gray-100 dark:border-dark-border focus:border-brand-500'
                        }`}
                      />
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        {nicknameStatus === 'checking' && <SafeIcon icon={FiLoader} className="text-gray-400 animate-spin" />}
                        {nicknameStatus === 'available' && <SafeIcon icon={FiCheck} className="text-emerald-500" />}
                        {nicknameStatus === 'taken' && <SafeIcon icon={FiX} className="text-red-500" />}
                      </div>
                    </div>
                    {nicknameStatus === 'taken' && <p className="text-[9px] text-red-500 font-bold uppercase tracking-tight ml-1">This nickname is already taken</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Date of Birth</label>
                      <div className="relative">
                        <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="date" value={formData.dob}
                          onChange={e => setFormData({ ...formData, dob: e.target.value })}
                          className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none focus:border-brand-500 dark:text-white transition-all"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Gender</label>
                      <select
                        value={formData.gender}
                        onChange={e => setFormData({ ...formData, gender: e.target.value })}
                        className="w-full px-4 py-3.5 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none appearance-none dark:text-white transition-all"
                      >
                        <option>Male</option><option>Female</option><option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Country</label>
                    <div className="relative">
                      <SafeIcon icon={FiGlobe} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <select
                        value={formData.country}
                        onChange={e => setFormData({ ...formData, country: e.target.value })}
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none appearance-none dark:text-white transition-all"
                      >
                        {globalOptions.nationalities.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  {error && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-2xl border border-red-100 dark:border-red-900/30">
                      <SafeIcon icon={FiAlertCircle} /><span className="text-[10px] font-black uppercase tracking-tight">{error}</span>
                    </motion.div>
                  )}

                  <button type="button" onClick={handleNext} className="w-full py-4 bg-brand-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-200 dark:shadow-brand-900/20 flex items-center justify-center gap-2 hover:bg-brand-700 transition-all">
                    Next Step <SafeIcon icon={FiArrowRight} />
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <InputGroup label="Email Address" icon={FiMail} type="email" value={formData.email} onChange={v => setFormData({ ...formData, email: v })} placeholder="email@example.com" />
                  <div className="grid grid-cols-2 gap-4">
                    <InputGroup label="Password" icon={FiLock} type="password" value={formData.password} onChange={v => setFormData({ ...formData, password: v })} placeholder="••••••" />
                    <InputGroup label="Confirm" icon={FiLock} type="password" value={formData.confirmPassword} onChange={v => setFormData({ ...formData, confirmPassword: v })} placeholder="••••••" />
                  </div>

                  {error && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-2xl border border-red-100 dark:border-red-900/30">
                      <SafeIcon icon={FiAlertCircle} /><span className="text-[10px] font-black uppercase tracking-tight">{error}</span>
                    </motion.div>
                  )}

                  <div className="flex gap-3">
                    <button type="button" onClick={() => setStep(1)} className="flex-1 py-4 bg-gray-50 dark:bg-dark-card text-gray-500 dark:text-gray-400 rounded-2xl font-black text-xs uppercase tracking-widest border border-gray-100 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-border transition-all">
                      Back
                    </button>
                    <button type="submit" disabled={loading} className="flex-[2] py-4 bg-brand-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-200 dark:shadow-brand-900/20 flex items-center justify-center gap-2 disabled:opacity-70 hover:bg-brand-700 transition-all">
                      {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'Complete Adventure'}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-50 dark:border-dark-border text-center">
            <p className="text-[10px] text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest">
              Already have an account?{' '}
              <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:underline">Login</Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function InputGroup({ label, icon, value, onChange, placeholder, type = 'text' }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">{label}</label>
      <div className="relative group">
        <SafeIcon icon={icon} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-600 transition-colors" />
        <input
          type={type} required value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 dark:focus:border-brand-500/50 transition-all dark:text-white dark:placeholder-gray-600"
        />
      </div>
    </div>
  );
}